import { type ReactNode, useMemo, useState } from 'react';
import { FileInput } from 'lucide-react';
import { PermissionSlug } from '../../constants/featureFlags';
import type { VisitResponse } from '../../services/api/types.gen';
import { useStore } from '../../store';
import type { TaskHeaderSecondaryAction } from '../task/TaskHeaderControls';
import { useDeferredDialogOpener } from '../task/useDeferredDialogOpener';
import { OutsideRecordDialog } from './OutsideRecordDialog';

interface UseOutsideRecordActionArgs {
  visit: VisitResponse | null;
  isDeleted: boolean;
  /** The chart is open and the visit is not missed. */
  canStart: boolean;
  /** Reload the visit after the dialog saved something. */
  onChanged: () => void | Promise<void>;
}

interface OutsideRecordAction {
  actions: TaskHeaderSecondaryAction[];
  /** Render once, anywhere in the panel. */
  dialog: ReactNode;
}

/**
 * The "Outside record" Actions-menu entry and its dialog: offered on a Not
 * Started visit, or on one already completed with an outside record. The
 * dialog says so when the visit's type refuses it.
 */
export function useOutsideRecordAction({
  visit,
  isDeleted,
  canStart,
  onChanged,
}: UseOutsideRecordActionArgs): OutsideRecordAction {
  // Matches the routes' require_all_agency_permissions: super users pass.
  const hasPermission = useStore(state => state.hasPermission);
  const holdsSlug = hasPermission(PermissionSlug.TASKS_EDIT_OUTSIDE_RECORD);
  const openDeferred = useDeferredDialogOpener();
  const [open, setOpen] = useState(false);

  const offered =
    holdsSlug &&
    visit !== null &&
    !isDeleted &&
    (Boolean(visit.outside_record) ||
      (canStart && visit.status === 'not_started'));

  const actions = useMemo<TaskHeaderSecondaryAction[]>(
    () =>
      offered
        ? [
            {
              label: 'Outside record',
              icon: <FileInput className='h-4 w-4 mr-2' aria-hidden='true' />,
              onSelect: () =>
                openDeferred('outsideRecord', () => setOpen(true)),
            },
          ]
        : [],
    [offered, openDeferred]
  );

  // Mounted only while open; it stays mounted while a completion moves the
  // status, since `offered` is recomputed from the visit behind it.
  if (!visit || !open) return { actions, dialog: null };

  return {
    actions,
    dialog: (
      <OutsideRecordDialog
        open={open}
        onOpenChange={setOpen}
        taskId={visit.id}
        onChanged={onChanged}
      />
    ),
  };
}
